import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface AivaBannerCollapseContextType {
  isCollapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const AivaBannerCollapseContext = createContext<
  AivaBannerCollapseContextType | undefined
>(undefined);

/**
 * Remembers whether the hire minimised the Aiva banner, for as long as the page lives.
 *
 * Deliberately in memory rather than in `localStorage`: the choice is about this session's screen,
 * so a fresh load starts expanded. This mirrors `hasAnsweredPermissions` in `HomeContent`, which is
 * session-scoped for the same reason.
 */
export const AivaBannerCollapseProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  const value = useMemo(
    () => ({ isCollapsed, setCollapsed }),
    [isCollapsed, setCollapsed]
  );

  return (
    <AivaBannerCollapseContext.Provider value={value}>
      {children}
    </AivaBannerCollapseContext.Provider>
  );
};

/**
 * The collapse state for the Aiva banner, held above whatever renders it when a provider is present.
 *
 * Falls back to component-local state instead of throwing, because the banner is mounted by two
 * separate wrappers that swap when automation fails, and it must still work when rendered on its own.
 * Without a provider the choice simply does not outlive the component, which is what a caller that
 * never mounted one is asking for.
 */
export const useAivaBannerCollapse = (): AivaBannerCollapseContextType => {
  const provided = useContext(AivaBannerCollapseContext);
  const [localIsCollapsed, setLocalIsCollapsed] = useState(false);

  const localSetCollapsed = useCallback((collapsed: boolean) => {
    setLocalIsCollapsed(collapsed);
  }, []);

  const fallback = useMemo(
    () => ({ isCollapsed: localIsCollapsed, setCollapsed: localSetCollapsed }),
    [localIsCollapsed, localSetCollapsed]
  );

  return provided ?? fallback;
};
